'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const projectTypes = [
{ icon: 'HomeIcon', label: 'New Home Construction', desc: 'Full new build from foundations up' },
{ icon: 'WrenchScrewdriverIcon', label: 'Full Home Renovation', desc: 'Complete property transformation' },
{ icon: 'FireIcon', label: 'Kitchen Renovation', desc: 'Modern kitchen design and fit' },
{ icon: 'BeakerIcon', label: 'Bathroom Renovation', desc: 'Premium bathroom upgrades' },
{ icon: 'BuildingOfficeIcon', label: 'Office Fit-Out', desc: 'Commercial workspace design' },
{ icon: 'ArrowsPointingOutIcon', label: 'Room Extension', desc: 'Expand your living space' },
{ icon: 'PaintBrushIcon', label: 'Painting & Decorating', desc: 'Interior and exterior painting' },
{ icon: 'SwatchIcon', label: 'Flooring', desc: 'Hardwood, tile, and LVT fitting' }];


const howItWorks = [
{ step: '01', title: 'Create Your Project', desc: 'Define your construction needs, specify rooms and areas, upload photos and plans, and set your budget.', icon: 'PlusCircleIcon' },
{ step: '02', title: 'Get a Design', desc: 'Use our AI Design Studio, request a professional design, or upload your existing plans.', icon: 'SwatchIcon' },
{ step: '03', title: 'Compare Quotations', desc: 'Send requests to multiple verified contractors and compare detailed quotations side by side.', icon: 'DocumentTextIcon' },
{ step: '04', title: 'Pay & Track', desc: 'Pay a secure deposit, then make milestone payments as your project progresses.', icon: 'CreditCardIcon' }];


const featuredContractors = [
{ id: 'fc-1', name: 'Apex Build Solutions', location: 'Johannesburg & Gauteng', rating: 4.9, reviews: 124, completed: 187, services: ['Full Renovation', 'New Build', 'Kitchen'], verified: true, image: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=600&h=400&fit=crop', logo: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=80&h=80&fit=crop' },
{ id: 'fc-2', name: 'UrbanCraft Renovations', location: 'Cape Town & Western Cape', rating: 4.7, reviews: 89, completed: 143, services: ['Interior Design', 'Kitchen', 'Bathroom'], verified: true, image: 'https://images.unsplash.com/photo-1484154218962-a197022b5858?w=600&h=400&fit=crop', logo: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=80&h=80&fit=crop' },
{ id: 'fc-3', name: 'SolidStone Builders', location: 'Durban & KwaZulu-Natal', rating: 4.8, reviews: 156, completed: 234, services: ['Extensions', 'Loft Conversion', 'New Build'], verified: true, image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop', logo: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=80&h=80&fit=crop' }];


const testimonials = [
{ id: 't-1', name: 'Sarah M.', location: 'Sandton', role: 'Client', text: 'Robeli made our kitchen renovation so much easier. Comparing three quotations side by side saved us over R64,000. The milestone payment system meant we never felt at risk.', rating: 5, project: 'Complete Kitchen Renovation', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_11a24dd3c-1772204214772.png" },
{ id: 't-2', name: 'Tom H.', location: 'Umhlanga', role: 'Client', text: 'The project tracking is incredible. I could see exactly what had been spent, what was left, and what was coming up — all in one place. No more chasing contractors for updates.', rating: 5, project: 'Three-Bedroom Extension', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d141c94c-1769337261853.png" },
{ id: 't-3', name: 'Marcus T.', location: 'Johannesburg', role: 'Contractor', text: 'As a contractor, Robeli has transformed how we manage client relationships. The quotation builder is professional, milestone payments are always on time, and the project workspace keeps everything organised.', rating: 5, project: 'Apex Build Solutions', avatar: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=80&h=80&fit=crop' }];


const faqs = [
{ q: 'How does the 15% platform fee work?', a: 'Robeli adds a 15% project management and administration fee on top of the contractor\'s quotation. This covers platform management, payment protection, dispute resolution, and ongoing project oversight. The fee is clearly shown on all quotations — there are no hidden charges.' },
{ q: 'How are contractors verified?', a: 'Every contractor on Robeli submits company registration documents, insurance certificates, identity verification, and previous project evidence. Our team manually reviews each application. Verified contractors display a badge on their profile.' },
{ q: 'Are my payments protected?', a: 'All payments are processed through the Robeli platform. Deposits and milestone payments are held until the relevant work is approved. You never pay a contractor directly outside the platform.' },
{ q: 'Can I use my own design or architect\'s plans?', a: 'Absolutely. You can upload PDF plans, architectural drawings, 3D renders, and reference images. Alternatively, use our AI Design Studio or request a professional design from our in-house team.' },
{ q: 'What happens if there is a dispute?', a: 'Robeli provides a structured dispute resolution process. Either party can raise a dispute, submit evidence, and our team will review and mediate. All project communications and records are stored on the platform to support the process.' },
{ q: 'How many quotations can I request?', a: 'You can request quotations from as many verified contractors as you choose. There is no limit. We recommend requesting three to five quotations to ensure a competitive comparison.' }];


export default function LandingPageContent() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const router = useRouter();

  useEffect(() => {
    router.prefetch('/sign-up-login-screen');
  }, [router]);

  const handleLoginClick = () => {
    router.push('/sign-up-login-screen');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* ─── HEADER ─────────────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8 h-16 flex items-center gap-6">
          <Link href="/" className="flex items-center gap-2 shrink-0">
            <AppLogo size={36} />
            <span className="font-bold text-xl text-primary">Robeli</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 flex-1">
            {[
            { label: 'Home', href: '/' },
            { label: 'How It Works', href: '#how-it-works' },
            { label: 'Find Contractors', href: '#contractors' },
            { label: 'AI Design', href: '#ai-design' },
            { label: 'Marketplace', href: '#marketplace' },
            { label: 'For Contractors', href: '#for-contractors' }].
            map((item) =>
            <a key={`nav-${item.label}`} href={item.href} className="px-3 py-2 text-sm font-medium text-muted-foreground nav-link-hover rounded-lg hover:bg-muted/50 transition-all">
                {item.label}
              </a>
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-2 shrink-0">
            <button onClick={handleLoginClick} className="btn-ghost text-sm">Log In</button>
            <button onClick={handleLoginClick} className="btn-primary text-sm">Register</button>
          </div>

          <button className="lg:hidden ml-auto p-2 rounded-lg hover:bg-muted" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <Icon name={mobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={22} />
          </button>
        </div>

        {mobileMenuOpen &&
        <div className="lg:hidden border-t border-border bg-card px-4 py-3 space-y-1">
            {['How It Works', 'Find Contractors', 'AI Design', 'Marketplace', 'For Contractors'].map((label) =>
          <a key={`mob-${label}`} href={`#${label.toLowerCase().replace(/ /g, '-')}`} className="block py-2 text-sm font-medium text-muted-foreground hover:text-foreground" onClick={() => setMobileMenuOpen(false)}>
                {label}
              </a>
          )}
            <div className="pt-2 border-t border-border flex gap-2">
              <Link href="/sign-up-login-screen" className="flex-1 text-center py-2 text-sm font-semibold text-primary border border-primary rounded-lg">Log In</Link>
              <Link href="/sign-up-login-screen" className="flex-1 text-center py-2 text-sm font-semibold text-primary-foreground bg-primary rounded-lg">Register</Link>
            </div>
          </div>
        }
      </header>

      {/* ─── HERO ─────────────────────────────────────────────────────────────────── */}
      <section className="relative gradient-hero overflow-hidden">
        <div className="absolute inset-0 blob-navy opacity-50" />
        <div className="absolute top-20 right-0 w-96 h-96 blob-accent opacity-30" />
        <div className="relative max-w-screen-xl mx-auto px-4 lg:px-8 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="slide-up">
              <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 text-xs font-semibold px-3 py-1.5 rounded-full mb-6 border border-white/20">
                <Icon name="CheckBadgeIcon" size={14} className="text-accent" />
                Trusted by 600+ homeowners across the UK
              </div>
              <h1 className="text-hero-xl text-white mb-6">
                Design, hire, pay, and manage your construction project in one place.
              </h1>
              <p className="text-lg text-white/70 mb-8 leading-relaxed">
                Create or upload your design, compare quotations from verified contractors, make milestone-based payments, and track every stage of your construction or renovation project.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                <Link href="/sign-up-login-screen" className="btn-accent">
                  <Icon name="PlusCircleIcon" size={18} />
                  Start a Project
                </Link>
                <Link href="#contractors" className="btn-outline !text-white !border-white/40 hover:!bg-white/10">
                  <Icon name="UserGroupIcon" size={18} />
                  Find Contractors
                </Link>
              </div>
              <Link href="/sign-up-login-screen" className="text-sm text-white/60 hover:text-accent transition-colors underline-offset-4 hover:underline">
                Register as a Contractor →
              </Link>
            </div>

            <div className="relative hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden shadow-modal">
                <AppImage
                  src="https://images.unsplash.com/photo-1707258556470-e3459888bf4a"
                  alt="Modern kitchen renovation completed by Robeli verified contractor"
                  width={700}
                  height={500}
                  className="w-full object-cover"
                  priority />
                
                <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="bg-card/95 backdrop-blur-sm rounded-xl p-4 shadow-card">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-muted-foreground">Complete Kitchen Renovation</span>
                      <span className="badge-active text-[10px] font-bold px-2 py-0.5 rounded-full">Active</span>
                    </div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex-1">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-muted-foreground">Progress</span>
                          <span className="font-semibold text-foreground">78%</span>
                        </div>
                        <div className="progress-bar"><div className="progress-fill" style={{ width: '78%' }} /></div>
                      </div>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Total Value: <span className="font-semibold text-foreground">R552,000</span></span>
                      <span className="text-muted-foreground">Paid: <span className="font-semibold text-success">R441,600</span></span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating stats */}
              <div className="absolute -top-4 -right-4 bg-card rounded-xl p-3 shadow-card border border-border">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name="CheckCircleIcon" size={16} className="text-success" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-foreground">342+</p>
                    <p className="text-[10px] text-muted-foreground">Projects Completed</p>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-card rounded-xl p-3 shadow-card border border-border">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="StarIcon" size={16} className="text-accent" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-foreground">4.8 / 5.0</p>
                    <p className="text-[10px] text-muted-foreground">Average Rating</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats bar */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
            { value: '600+', label: 'Happy Clients' },
            { value: 'R192M+', label: 'Project Value Managed' },
            { value: '48', label: 'Verified Contractors' },
            { value: '96%', label: 'On-Time Completion' }].
            map((stat) =>
            <div key={`stat-${stat.label}`} className="text-center">
                <p className="text-2xl lg:text-3xl font-bold text-white font-tabular">{stat.value}</p>
                <p className="text-sm text-white/60 mt-1">{stat.label}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─────────────────────────────────────────────────────────── */}
      <section id="how-it-works" className="py-20 bg-background">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-14">
            <p className="section-label mb-3">The Process</p>
            <h2 className="text-section-title text-foreground">How Robeli Works</h2>
            <p className="text-muted-foreground mt-3 max-w-xl mx-auto">From project idea to final handover — one connected platform manages every step.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((step, i) =>
            <div key={`step-${step.step}`} className="relative">
                {i < howItWorks.length - 1 &&
              <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-border to-transparent z-0" />
              }
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mb-4 shadow-card">
                    <Icon name={step.icon as any} size={28} className="text-primary-foreground" />
                  </div>
                  <div className="text-xs font-bold text-accent mb-2 tracking-widest">{step.step}</div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ─── PROJECT TYPES ─────────────────────────────────────────────────────────── */}
      <section className="py-20 bg-secondary">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-14">
            <p className="section-label mb-3">What We Cover</p>
            <h2 className="text-section-title text-foreground">Every Type of Construction Project</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {projectTypes.map((pt) =>
            <div key={`pt-${pt.label}`} className="card-elevated card-hover p-5 cursor-pointer group">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center mb-3 group-hover:bg-primary/20 transition-colors">
                  <Icon name={pt.icon as any} size={20} className="text-primary" />
                </div>
                <h3 className="text-sm font-semibold text-foreground mb-1">{pt.label}</h3>
                <p className="text-xs text-muted-foreground">{pt.desc}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ─── AI DESIGN ─────────────────────────────────────────────────────────────── */}
      <section id="ai-design" className="py-20 bg-background">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="section-label mb-3">AI Design Studio</p>
              <h2 className="text-section-title text-foreground mb-5">Visualise Your Space Before Construction Begins</h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">Upload a photo of your existing space, choose your style, and generate multiple design concepts in minutes. Share your favourite directly with contractors.</p>
              <ul className="space-y-3 mb-8">
                {['Upload existing room photo', 'Select design style and materials', 'Generate 3 design concepts', 'Compare and choose your favourite', 'Share directly with your contractor'].map((item) =>
                <li key={`ai-${item}`} className="flex items-center gap-3 text-sm">
                    <div className="w-5 h-5 rounded-full bg-success/10 flex items-center justify-center shrink-0">
                      <Icon name="CheckIcon" size={12} className="text-success" />
                    </div>
                    <span className="text-foreground">{item}</span>
                  </li>
                )}
              </ul>
              <Link href="/sign-up-login-screen" className="btn-primary">
                <Icon name="SparklesIcon" size={18} />
                Try AI Design Studio
              </Link>
            </div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-3">
                  <div className="rounded-xl overflow-hidden shadow-card">
                    <AppImage src="https://img.rocket.new/generatedImages/rocket_gen_img_18ded5bf4-1764766015972.png" alt="Before: outdated kitchen with old cabinets and worn countertops" width={400} height={280} className="w-full object-cover" />
                    <div className="p-2 bg-muted/60 text-[10px] font-semibold text-muted-foreground text-center">BEFORE</div>
                  </div>
                  <div className="rounded-xl overflow-hidden shadow-card">
                    <AppImage src="https://img.rocket.new/generatedImages/rocket_gen_img_18138f727-1772474830922.png" alt="AI generated design concept showing modern kitchen layout" width={400} height={200} className="w-full object-cover" />
                    <div className="p-2 bg-primary/10 text-[10px] font-semibold text-primary text-center">AI CONCEPT 2</div>
                  </div>
                </div>
                <div className="space-y-3 pt-6">
                  <div className="rounded-xl overflow-hidden shadow-card">
                    <AppImage src="https://img.rocket.new/generatedImages/rocket_gen_img_18138f727-1772474830922.png" alt="AI generated design concept showing luxury kitchen with island" width={400} height={280} className="w-full object-cover" />
                    <div className="p-2 bg-accent/10 text-[10px] font-semibold text-accent-foreground text-center">AI CONCEPT 1 ✓</div>
                  </div>
                  <div className="rounded-xl overflow-hidden shadow-card">
                    <AppImage src="https://img.rocket.new/generatedImages/rocket_gen_img_18138f727-1772474830922.png" alt="AI generated design concept showing minimalist kitchen design" width={400} height={200} className="w-full object-cover" />
                    <div className="p-2 bg-muted/60 text-[10px] font-semibold text-muted-foreground text-center">AI CONCEPT 3</div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-3 -right-3 bg-accent rounded-xl px-3 py-2 shadow-card">
                <p className="text-xs font-bold text-accent-foreground">From R464</p>
                <p className="text-[10px] text-accent-foreground/70">per design session</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PLATFORM FEE ─────────────────────────────────────────────────────────── */}
      <section className="py-20 bg-primary">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="section-label mb-3 !text-accent">Platform Service</p>
              <h2 className="text-section-title text-white mb-5">Transparent 15% Project Management Fee</h2>
              <p className="text-white/70 mb-6 leading-relaxed">
                Robeli adds a clear 15% project management and administration fee to every quotation. This funds payment protection, dispute resolution, platform oversight, and dedicated project support — never hidden, always transparent.
              </p>
              <div className="bg-white/10 rounded-xl p-5 border border-white/20 mb-6">
                <h4 className="text-white font-semibold mb-4 text-sm">Example Quotation Breakdown</h4>
                <div className="space-y-2">
                  {[
                  { label: 'Contractor Quotation', value: 'R160,000', muted: false },
                  { label: 'Platform Management Fee (15%)', value: 'R24,000', muted: true },
                  { label: 'Total Client Amount', value: 'R184,000', muted: false, bold: true }].
                  map((row) =>
                  <div key={`fee-${row.label}`} className={`flex justify-between ${row.bold ? 'border-t border-white/20 pt-2 mt-2' : ''}`}>
                      <span className={`text-sm ${row.muted ? 'text-white/50' : 'text-white/80'}`}>{row.label}</span>
                      <span className={`text-sm font-semibold ${row.bold ? 'text-accent' : 'text-white'}`}>{row.value}</span>
                    </div>
                  )}
                </div>
              </div>
              <ul className="space-y-2">
                {['Payment protection on all milestones', 'Dispute resolution support', 'Dedicated project oversight', 'Verified contractor network', 'Platform-managed communications'].map((item) =>
                <li key={`fee-li-${item}`} className="flex items-center gap-2 text-sm text-white/80">
                    <Icon name="CheckCircleIcon" size={14} className="text-accent shrink-0" />
                    {item}
                  </li>
                )}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
              { icon: 'ShieldCheckIcon', title: 'Payment Protection', desc: 'Deposits and milestones held securely until work is approved.' },
              { icon: 'ScaleIcon', title: 'Dispute Resolution', desc: 'Neutral mediation if disagreements arise during a project.' },
              { icon: 'EyeIcon', title: 'Project Oversight', desc: 'Our team monitors every active project for delays and issues.' },
              { icon: 'ChatBubbleLeftRightIcon', title: 'Managed Communications', desc: 'All client-contractor contact stays on the platform.' }].
              map((card) =>
              <div key={`service-${card.title}`} className="bg-white/10 border border-white/20 rounded-xl p-4">
                  <div className="w-9 h-9 bg-accent/20 rounded-lg flex items-center justify-center mb-3">
                    <Icon name={card.icon as any} size={18} className="text-accent" />
                  </div>
                  <h4 className="text-white font-semibold text-sm mb-1">{card.title}</h4>
                  <p className="text-white/60 text-xs leading-relaxed">{card.desc}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ─── FEATURED CONTRACTORS ─────────────────────────────────────────────────── */}
      <section id="contractors" className="py-20 bg-background">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="section-label mb-3">Verified Professionals</p>
              <h2 className="text-section-title text-foreground">Featured Contractors</h2>
            </div>
            <Link href="/sign-up-login-screen" className="btn-outline text-sm hidden md:flex">
              Browse All Contractors
              <Icon name="ArrowRightIcon" size={16} />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {featuredContractors.map((c) =>
            <div key={c.id} className="card-elevated card-hover overflow-hidden group">
                <div className="relative h-44 overflow-hidden">
                  <AppImage src={c.image} alt={`${c.name} portfolio — completed construction project`} width={600} height={400} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/50 to-transparent" />
                  {c.verified &&
                <div className="absolute top-3 right-3 bg-card/90 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
                      <Icon name="CheckBadgeIcon" size={12} className="text-success" />
                      <span className="text-[10px] font-bold text-success">Verified</span>
                    </div>
                }
                  <div className="absolute bottom-3 left-3">
                    <img src={c.logo} alt={`${c.name} company logo`} className="w-10 h-10 rounded-lg object-cover border-2 border-white" />
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-foreground mb-0.5">{c.name}</h3>
                  <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1">
                    <Icon name="MapPinIcon" size={11} />
                    {c.location}
                  </p>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {c.services.slice(0, 3).map((s) =>
                  <span key={`cs-${c.id}-${s}`} className="text-[10px] font-medium bg-secondary text-muted-foreground px-2 py-0.5 rounded-full">{s}</span>
                  )}
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                    <div className="flex items-center gap-1">
                      <Icon name="StarIcon" size={12} className="text-accent" />
                      <span className="font-semibold text-foreground">{c.rating}</span>
                      <span>({c.reviews})</span>
                    </div>
                    <span>{c.completed} projects completed</span>
                  </div>
                  <Link href="/sign-up-login-screen" className="btn-primary w-full justify-center text-sm py-2">
                    Request Quote
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ─── FOR CONTRACTORS ──────────────────────────────────────────────────────── */}
      <section id="for-contractors" className="py-20 bg-secondary">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div className="relative rounded-2xl overflow-hidden shadow-modal">
              <AppImage src="https://img.rocket.new/generatedImages/rocket_gen_img_1d1a57446-1785245829201.png" alt="Construction contractor reviewing project plans on Robeli platform" width={700} height={500} className="w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/70 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-card/95 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
                      <Icon name="CurrencyPoundIcon" size={16} className="text-success" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Expected This Month</p>
                      <p className="text-lg font-bold text-foreground font-tabular">R390,000</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><p className="text-sm font-bold text-foreground">4</p><p className="text-[10px] text-muted-foreground">Active Projects</p></div>
                    <div><p className="text-sm font-bold text-foreground">2</p><p className="text-[10px] text-muted-foreground">New Requests</p></div>
                    <div><p className="text-sm font-bold text-foreground">96%</p><p className="text-[10px] text-muted-foreground">Completion Rate</p></div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <p className="section-label mb-3">For Contractors</p>
              <h2 className="text-section-title text-foreground mb-5">Grow Your Business with Robeli</h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">Join a network of verified professionals and access a steady stream of qualified project enquiries. Our platform handles payments, communications, and project management — so you can focus on building.</p>
              <ul className="space-y-4 mb-8">
                {[
                { icon: 'InboxIcon', title: 'Receive Qualified Enquiries', desc: 'Clients come to you with defined scopes, budgets, and design files — no time wasted on vague requests.' },
                { icon: 'DocumentTextIcon', title: 'Professional Quotation Builder', desc: 'Build detailed, itemised quotations with our built-in tool. Clients see a professional presentation every time.' },
                { icon: 'CreditCardIcon', title: 'Guaranteed Milestone Payments', desc: 'Payments are processed through the platform. No chasing invoices — funds released when milestones are approved.' },
                { icon: 'ChartBarIcon', title: 'Project Management Tools', desc: 'Manage phases, milestones, progress reports, and expenses from one contractor dashboard.' }].
                map((item) =>
                <li key={`fc-${item.title}`} className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                      <Icon name={item.icon as any} size={16} className="text-primary" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-0.5">{item.title}</h4>
                      <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                    </div>
                  </li>
                )}
              </ul>
              <Link href="/sign-up-login-screen" className="btn-primary">
                <Icon name="BuildingOfficeIcon" size={18} />
                Register as a Contractor
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─────────────────────────────────────────────────────────── */}
      <section className="py-20 bg-background">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-14">
            <p className="section-label mb-3">Reviews</p>
            <h2 className="text-section-title text-foreground">What Our Users Say</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) =>
            <div key={t.id} className="card-elevated p-6">
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: 5 }).map((_, si) =>
                <Icon key={`star-${t.id}-${si}`} name="StarIcon" size={14} className="text-accent" variant="solid" />
                )}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5 italic">"{t.text}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-border">
                  <img src={t.avatar} alt={`${t.name} testimonial profile photo`} className="w-9 h-9 rounded-full object-cover" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.role} · {t.project}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ─── FAQ ──────────────────────────────────────────────────────────────────── */}
      <section className="py-20 bg-secondary">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-14">
            <div>
              <p className="section-label mb-3">FAQ</p>
              <h2 className="text-section-title text-foreground mb-4">Frequently Asked Questions</h2>
              <p className="text-muted-foreground text-sm leading-relaxed mb-6">Can't find what you're looking for? Our support team is here to help.</p>
              <Link href="/sign-up-login-screen" className="btn-outline text-sm">Contact Support</Link>
            </div>
            <div className="lg:col-span-2 space-y-3">
              {faqs.map((faq, i) =>
              <div key={`faq-${i}`} className="card-elevated overflow-hidden">
                  <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left">
                  
                    <span className="text-sm font-semibold text-foreground pr-4">{faq.q}</span>
                    <Icon name={openFaq === i ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={16} className="text-muted-foreground shrink-0" />
                  </button>
                  {openFaq === i &&
                <div className="px-5 pb-5 fade-in">
                      <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
                    </div>
                }
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ─── CTA ──────────────────────────────────────────────────────────────────── */}
      <section className="py-20 gradient-hero">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-section-title text-white mb-4">Ready to Start Your Project?</h2>
          <p className="text-white/70 mb-8 max-w-xl mx-auto">Join thousands of homeowners who have used Robeli to find great contractors and deliver successful projects.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link href="/sign-up-login-screen" className="btn-accent">
              <Icon name="PlusCircleIcon" size={18} />
              Start a Project
            </Link>
            <Link href="/sign-up-login-screen" className="btn-outline !text-white !border-white/40 hover:!bg-white/10">
              <Icon name="BuildingOfficeIcon" size={18} />
              Register as a Contractor
            </Link>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ───────────────────────────────────────────────────────────────── */}
      <footer className="bg-foreground text-white/60 py-14">
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10 mb-10">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <AppLogo size={36} />
                <span className="font-bold text-xl text-white">Robeli</span>
              </div>
              <p className="text-sm leading-relaxed mb-4">South Africa's trusted construction marketplace and project management platform. Connecting clients with verified contractors since 2024.</p>
              <p className="text-xs">© 2026 Robeli (Pty) Ltd. All rights reserved.</p>
            </div>
            {[
            { heading: 'Platform', links: ['How It Works', 'Find Contractors', 'AI Design Studio', 'Marketplace', 'Pricing'] },
            { heading: 'For Clients', links: ['Create a Project', 'Compare Quotes', 'Track Progress', 'Milestone Payments', 'Design Services'] },
            { heading: 'Legal', links: ['Terms of Service', 'Privacy Policy', 'Cookie Policy', 'Contractor Agreement', 'Dispute Policy'] }].
            map((col) =>
            <div key={`footer-${col.heading}`}>
                <h4 className="text-white font-semibold text-sm mb-4">{col.heading}</h4>
                <ul className="space-y-2">
                  {col.links.map((link) =>
                <li key={`fl-${link}`}><a href="#" className="text-sm hover:text-white transition-colors">{link}</a></li>
                )}
                </ul>
              </div>
            )}
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs">Robeli (Pty) Ltd · Registered in South Africa · CIDB Registration No. ZA-2024-8473</p>
            <p className="text-xs">All contractor payments are processed securely through the Robeli platform.</p>
          </div>
        </div>
      </footer>
    </div>);

}